import React from 'react';
import { SchematicShell, Circle, Rect } from './SchematicPrimitives';
import {
  LP_DIAMETER,
  LP_GAP,
  LP_LABEL_FRAC,
  NEON_PANEL_HEIGHT_FRAC,
  NEON_PANEL_WIDTH_FRAC,
  neonWavePoints,
  recordsAcross,
} from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * 80's-retro wall decor, 2D. See DormCoastalSchematic for why these exist.
 *
 * The record COUNT is derived by the shared `recordsAcross`, not re-guessed: a
 * 12" LP is 12" whatever wall it lands on, so a wider panel gets more records
 * rather than bigger ones — and an elevation showing four where the room shows
 * six is worse than showing none.
 */
export function DormVinylWallSchematic({
  widthMeters: width,
  heightMeters: height,
  strokeColor,
  strokeWidth,
}) {
  const cols = recordsAcross(width);
  const rows = recordsAcross(height);
  const r = LP_DIAMETER / 2;
  const pitch = LP_DIAMETER + LP_GAP;
  const originX = width / 2 - ((cols - 1) * pitch) / 2;
  const originY = height / 2 - ((rows - 1) * pitch) / 2;

  const discs = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      discs.push({ cx: originX + col * pitch, cy: originY + row * pitch });
    }
  }

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      {discs.map((d, i) => (
        <g key={i}>
          <Circle
            cx={d.cx}
            cy={d.cy}
            r={r}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
          <Circle
            cx={d.cx}
            cy={d.cy}
            r={r * LP_LABEL_FRAC}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth * 0.6}
          />
        </g>
      ))}
    </SchematicShell>
  );
}

export function DormNeonSignSchematic({
  widthMeters: width,
  heightMeters: height,
  strokeColor,
  strokeWidth,
}) {
  // The SAME squiggle the tube sweeps — `neonWavePoints` is pure and shared, so
  // retuning the wave moves both drawings or neither.
  const pts = neonWavePoints(
    width * NEON_PANEL_WIDTH_FRAC,
    height * NEON_PANEL_HEIGHT_FRAC,
    48
  );
  const d = pts
    .map(
      (p, i) =>
        // Centre the curve in the panel and flip to SVG's +y-down space.
        `${i === 0 ? 'M' : 'L'} ${width / 2 + p.x} ${height / 2 - p.y}`
    )
    .join(' ');

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <path
        d={d}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth * 1.4}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </SchematicShell>
  );
}
